/**
 * I/O 境界の関数が DOM 例外を変換して throw する独自 Error。
 *
 * 設計の根拠は要件書 §5.8 (Boundary-Concerns 軽量版)。
 * 中間関数は try/catch も Result も書かず、catch する場所は 2 種類だけ:
 *   1. user-initiated entry point (ボタンハンドラ等)
 *   2. runtime-initiated leaf (rAF / setInterval / Worker.onmessage / event)
 *
 * Worker 境界だけは plain object Result で送る ({ ok, code, policy, message?, value? })。
 * その Result 型 (`WorkerResult<T>`) と AppError ⇔ Result の変換 (`workerResultToError` /
 * `errorToWorkerResult`) は worker の wire format なので `worker-protocol.ts` 側に置く。
 * 本ファイルは AppError クラスと、その語彙 (`AppErrorCode` / `AppErrorPolicy`) だけを持つ。
 */

/**
 * I/O 失敗の種類を識別する短い文字列リテラル列挙。
 * `handleAppError` の switch で網羅し、追加時は switch も追従させる。
 */
export type AppErrorCode =
  | "CameraDenied"
  | "CameraStreamLost"
  | "MediaPipeInitFailed"
  | "MediaPipeRuntimeError"
  | "FrameDropped"
  | "IndexedDBQuotaExceeded"
  | "IndexedDBUnavailable"
  | "IndexedDBError"
  | "WorkerRpcInFlight"
  | "PipUnsupported"
  | "PipRequestFailed"
  | "PipStylesheetNoRealm"
  | "MediaRecorderError"
  | "CorpusFsPermissionDenied"
  | "CorpusFsAborted"
  | "LabelsSchemaInvalid"
  | "LabelsVersionMismatch"
  | "CorpusWriteError"
  | "CorpusReplayDecodeError"
  | "Unexpected";

/**
 * catch 側がどう扱うかのラベル。
 *
 * - `fatal`: ループ停止 + ユーザー通知
 * - `fallback`: 別モードへ切り替えて継続 (例: DB → メモリ)
 * - `transient`: 1 フレーム / 1 操作スキップして次へ
 */
export type AppErrorPolicy = "fatal" | "fallback" | "transient";

export class AppError extends Error {
  readonly code: AppErrorCode;
  readonly policy: AppErrorPolicy;

  constructor(code: AppErrorCode, policy: AppErrorPolicy, message?: string, cause?: unknown) {
    super(message ?? code, cause === undefined ? undefined : { cause });
    this.code = code;
    this.policy = policy;
    this.name = "AppError";
  }
}

export function isAppError(err: unknown): err is AppError {
  return err instanceof AppError;
}
